/*
  Allowed formatting tags:
  <emph> : emphasize the important part of the text
 */

export enum MESSAGE_ACTION {
  ADD = "add",
  MODIFY = "modify",
  PUBLISH = "publish",
  DELETE = "delete",
}

export const messages = {
  /* LOGIN & AUTH */
  "login.welcome": "Welcome",
  "auth.password.modified.successTitle": "Password Modified", //It appears that these titles do not usually contain periods, so I removed the period
  "auth.password.modified.successMessage":
    "Your password was successfully changed. Please login with your new password.",
  "auth.password.set.successTitle": "Password Set", //removed the period
  "auth.password.set.successMessage": "Your password was successfully registered. You can now sign in to Selector.",

  /* APP CONFIGURATION */
  "config.application.htmlPreview.strict.title": "{mode} Mode Enabled",
  "config.application.htmlPreview.strict.message":
    "In strict mode the preview might have some limitations for security reasons.",

  /* DASHBOARD */
  "dashboard.gettingStarted.welcome": "Welcome to Genedata Selector!",
  "dashboard.gettingStarted.openContent":
    "Open the <tip>Content</tip> menu to explore the content that is available to you in the system.",
  "dashboard.update.successTitle": "Home Page Updated",
  "dashboard.update.successMessage": "Changes were successfully saved",
  "dashboard.update.failureTitle": "Changes Could Not Be Saved to Your Home Page",

  /* SAVED ITEMS */
  "view.save.successTitle": "View Saved",
  "view.save.successMessage": "<emph>{viewName}</emph> was successfully saved to <link>{projectName}</link>",
  "view.update.successMessage": "<emph>{viewName}</emph> was successfully updated in <link>{projectName}</link>",
  "view.copy.successTitle": "{viewCount, plural, one{View} other {Views}} Copied",
  "view.copy.successMessage":
    "{viewsCount} {viewsCount, plural, one {view was} other {views were}} successfully saved to <link>{projectName}</link>",
  "view.delete.successTitle": "{viewCount, plural, one{View} other {Views}} Deleted",
  "view.delete.successMessage":
    "{viewCount, plural, one {<emph># view</emph> was} other {<emph># views</emph> were}} successfully deleted",
  "view.delete.errorTitle": "View Deletion Failed",
  "view.delete.errorMessage": "Deletion of <emph>{viewsCount, plural, one {# view} other {# views}}</emph> failed",
  "view.placeholder":
    "When you're working, look for the option to save pages, flags, genomes, etc. so you can access, organize or analyse them from here in the future.",

  "sampleParent.delete.successTitle": "{parentTypeName} Deleted",
  "sampleParent.delete.successMessage":
    "{parentTypesCount, plural, one {<emph># {parentTypeName}</emph> was} other {<emph># {parentTypeName}</emph> were}} successfully deleted",
  "sampleParent.edit.successTitle": "{parentTypeName} Saved",
  "sampleParent.edit.successMessage": "<emph>{parentName}</emph> was successfully saved",
  "group.save.successTitle": "Group Saved",
  "group.save.successMessage":
    "Group <groupLink>{groupName}</groupLink> was saved to <projectLink>{projectName}</projectLink>",
  "group.delete.successTitle": "{groupCount, plural, one {Group} other {Groups}} Deleted",
  "group.delete.successMessage":
    "{groupCount, plural, one {<emph># group</emph> was} other {<emph># groups</emph> were}} successfully deleted",
  "group.delete.errorTitle": "Group Deletion Failed",
  "group.delete.errorMessage": "Deletion of <emph>{groupsCount, plural, one {# Group} other {# Groups}}</emph> Failed",
  "group.copy.successTitle": "{groupCount, plural, one {Group} other {Groups}} Copied",
  "group.copy.successMessage":
    "{groupsCount} {groupType} {groupsCount, plural, one {group was} other {groups were}} successfully saved to <link>{projectName}</link>",
  "flag.deleted.successTitle": "{flagsCount, plural, one {Flag} other {Flags}} Deleted",
  "flag.deleted.successMessage":
    "{flagsCount, plural, one {<emph># Flag</emph> was} other {<emph># Flags</emph> were}} successfully deleted",
  "flag.deleted.errorTitle": "Flag Deletion Failed",
  "flag.deleted.errorMessage": "Deletion of <emph>{flagsCount, plural, one {# flag} other {# flags}}</emph> failed",
  "flag.copy.successTitle": "{flagsCount, plural, one {Flag} other {Flags}} Copied",
  "flag.copy.successMessage":
    "{flagsCount} {flagsCount, plural, one {flag was} other {flags were}} successfully saved to <link>{projectName}</link>",
  "flag.update.errorTitle": "Flag Update Failed",
  "flag.update.errorMessage": "Unable to update flag with id <emph>{flagId}</emph>",
  "flag.saved.successTitle": "{flagsCount, plural, one {Flag} other {Flags}} Created",
  "flag.saved.successMessage": "<link>{flagName}</link> was successfully created",
  "coordinateList.deleted.successTitle":
    "{coordinateListCount, plural, one {Coordinate List} other {Coordinate Lists}} Deleted",
  "coordinateList.deleted.successMessage":
    "{coordinateListCount, plural, one {<emph># Coordinate list</emph> was} other {<emph># Coordinate lists</emph> were}} successfully deleted",
  "coordinateList.deleted.errorTitle": "Coordinate List Deletion Failed",
  "coordinateList.deleted.errorMessage":
    "Deletion of <emph>{coordinateListCount, plural, one {# coordinate list} other {# coordinate lists}}</emph> failed",

  /* FILE BROWSER */
  "filesPage.projectNotAccessible.errorTitle": "Project Not Accessible",
  "filesPage.projectNotAccessible.errorMessage": "The project you would like to access is not available",
  "filesPage.uploadValidation.errorTitle": "Upload Error",
  "filesPage.uploadValidation.rejectedTypeMessage": "Files of type [{type}] cannot be uploaded",
  "filesPage.uploadValidation.errorMessage":
    "{duplicateCount, plural, one {File} other {Files}} {duplicates} cannot be uploaded because a file with the same name exists at this location.",

  "file.delete.successTitle": "{fileCount, plural, one {Item} other {Items}} Deleted",
  "file.delete.successMessage":
    "{fileCount, plural, one {<emph># item</emph> was} other {<emph># items</emph> were}} successfully deleted",
  "file.upload.successTitle": "File Uploaded",
  "file.upload.successMessage": "File {fileName} was successfully uploaded to {targetLocation}",
  "file.copy.successTitle": "{filesCount, plural, one {Item} other {Items}} Copied",
  "file.copy.successMessage":
    "{filesCount, plural, one {Item was} other {Items were}} successfully copied to {targetFolderPath}",
  "file.moved.successTitle": "{filesCount, plural, one {Item} other {Items}} Moved",
  "file.moved.successMessage":
    "{filesCount, plural, one {Item was} other {Items were}} successfully moved to {targetFolderPath}",

  "file.preview.unsupported": "File type ''{fileType}'' not supported for preview",
  "file.download.failed.title": "Operation failed", // The operation could be either download, cut or copy
  "file.download.failed.children":
    "{filesCount, plural, one {File} other {Files}} ''{fileNames}'' could not be found in the file system. Please contact your administrator.",

  /* FILE INDEXING*/
  "file.indexing.title": "Indexing",
  "file.indexing.startedMessage": "Indexing of file {fileName} started",
  "file.indexing.finishedMessage": "Indexing of file {fileName} finished",
  "file.index.exists": "This file is already indexed.",
  "file.index.indexing": "This file is currently being indexed.",
  "file.index.missing": "Creating an index on this file is a prerequisite for visualizing it.",
  "file.index.cancelled": "Creating an index on this file is a prerequisite for visualizing it.",
  "file.index.failed": "Indexing of this file previously failed.",

  /*FILE INDEXING - SEND TO GENOME VIEWER*/
  "genomeViewer.file.index.indexing":
    "The file is currently being indexed. You may view the data after the indexing has completed.",
  "genomeViewer.file.index.missing": "Please index this file before viewing it.",
  "genomeViewer.file.index.cancelled": "Please index this file before viewing it.",
  "genomeViewer.file.index.failed": "Please index this file again before viewing it.",

  /* PROJECTS */
  "projects.create.successTitle": "{projectLabel} Created",
  "projects.create.public.successMessage": "<link>{projectName}</link> is a public project",
  "projects.create.private.successMessage": "<link>{projectName}</link> is only visible to you",
  "projects.create.open.successMessage":
    "{membersCount, plural, one {# member was} other {# members were}} added to <link>{projectName}</link>",
  "projects.modify.successTitle": "{projectLabel} Updated",
  "projects.modify.successMessage": "<link>{projectName}</link> was successfully updated",
  "projects.delete.successTitle": "{projectLabel} Deleted",
  "projects.delete.successMessage": "<emph>{projectName}</emph> was successfully deleted",
  "projects.private.disabled": "Private saving is disabled for this project",
  "projects.private.itemsPrivacy": "Items in the Private Workspace are never shared with other users.",

  /* GENOME VIEWER */
  // NGS coverage track.
  "genomeViewer.ngs.coverageTrack.alignmentValidation.warningTitle": "Alignment Validation",

  "genomeViewer.saveTrackLayout.successTitle": "Track Layout Saved",
  "genomeViewer.saveTrackLayout.successMessage": "The track layout was successfully saved",

  // TRACK EXPRESSION COLORING
  "genomeViewer.expressionColoring.warningTitle": "Dataset Not Available",
  "genomeViewer.expressionColoring.warningMessage":
    "Some content in this saved view cannot be displayed. A dataset has been removed or permissions have been revoked.",

  /* ANALYTICAl DATA VIEWER */
  "dataViewer.viewLoading.warningTitle": "Dataset Not Available",
  "dataViewer.viewLoading.warningMessage":
    "Some content in this saved view cannot be displayed. A dataset has been removed or permissions have been revoked.",
  // VENN DIAGRAM
  "vennDiagram.limitExceeded.warningTitle": "Two Group Limit",
  "vennDiagram.limitExceeded.warningMessage": "Exactly two groups must be selected for the Venn diagram comparison",

  // first check the availability of all rows to be selected
  "table.select.warningTitle": "Unable to Select", // multiple usages in table-data.ts
  "table.select.TableRange.warningMessage":
    "The selection contains rows that have not been loaded from the server. Please scroll through the data and try again.",

  // do not launch analyzer when launch limit exceeded
  "table.forward.toAnalyzer.warningTitle": "{featureName} Limit Exceeded",
  "table.select.toggle.warningMessage": "Select all requires that the list has fewer than {maxGridSelection, number} items.",
  "table.forward.toAnalyzer.warningMessage": "The Analyzer is unable to be called with more than {launchLimit} items",

  // do not launch analyzer from entities without pathway association
  "table.forward.toAnalyzer.translateAnalyzerIds.warningTitle": "No Pathway Association",
  "table.forward.toAnalyzer.translateAnalyzerIds.warningMessage":
    "{analyzer} cannot be launched because the selected {featureName} are not associated with any pathways",

  // do not launch locus analyzer from non-genomic transcripts and proteins
  "table.forward.toAnalyzer.noGene.warningTitle": "No Gene Association",
  "table.forward.toAnalyzer.noGene.warningMessage":
    "{analyzer} cannot be launched because the selected {featureName} are not associated with any genes",
  "table.forward.toAnalyzer.noSequence.warningTitle": "No Sequence",
  "table.forward.toAnalyzer.noSequence.warningMessage":
    "{analyzer} cannot be launched because the selected {featureName} are not associated with any sequences",
  "table.forward.toAnalyzer.noOrganism.warningTitle": "No Organism Association",
  "table.forward.toAnalyzer.noOrganism.warningMessage":
    "{analyzer} cannot be launched because the selected {featureName} are not associated with any organisms",

  // Missing genome association
  "table.forward.nonGenomicGene.warningTitle": "No Genome Association",
  "table.forward.nonGenomicGene.warningMessage":
    "At least one of the selected genes does not have a genome association",
  "table.forward.singleGeneNoGenomeAssociation.warningMessage": "The selected gene does not have a genome association",

  /* SEARCH */

  /* PLAYBOOKS & EVENTS */
  "playbook.execution.parentStatusUpdate":
    "<emph>{sampleParentName}</emph> status has changed to <emph>{status}</emph>.",
  "playbook.execution.eventStatusUpdate":
    "<emph>{playbookEventName}</emph> status has changed to <emph>{status}</emph>.",
  "playbook.execution.workflowEventStatusUpdate":
    "<emph>{workflowEventName}</emph> status has changed to <emph>{status}</emph>.",
  "playbook.submission.successTitle": "{isSampleAnalysis, select, true {Samples Saved} other {Analysis Submitted}}",
  "playbook.submission.sampleContext.successMessage": "Samples for <link>{parentName}</link> were successfully saved",
  "playbook.submission.successMessage": "''<emph>{playbookName}</emph>'' was successfully submitted",
  "playbook.execution.error": "Sorry, a configuration issue is preventing the Playbook from being executed",
  "playbook.execution.error.missingFileAttribute":
    "A sample attribute needs to be configured to handle the NGS files that will be used with the NGS workflow. Please review the Playbook configuration.",
  "playbook.execution.error.hiddenFileAttribute":
    "An NGS file attribute must be included in the list of visible sample attributes",
  "playbook.execution.error.missingPrimaryParent":
    "A primary parent was not defined for the sample type ''{sampleTypeName}''. Please review the sample type configuration.",
  "playbook.execution.error.notAuthorized": "Your Selector account is not authorized to access the Processor module",
  "playbook.execution.error.workflowNotFound":
    "NGS workflow ''{workflowId}'' cannot be found.  Please review the workflow in Processor or modify the Playbook configuration.",
  "playbook.execution.error.invalidWorkflow":
    "NGS workflow ''{workflowName}'' is invalid.  Please review the workflow in Processor or modify the Playbook configuration.",
  "playbook.execution.error.deprecatedApproval":
    "NGS workflow ''{workflowName}'' was approved after the Playbook was configured. Please review the configuration for the Playbook.",
  "playbook.execution.error.missingInputFilesParam":
    "NGS workflow ''{workflowName}'' does not have a file input parameter included in the approval. Please review the workflow in Processor.",
  "playbook.execution.warning.event.fileResourcesUnavailable":
    "This Playbook cannot be executed with the exact same input because some file resources of the event are not available to you.",
  "playbook.execution.warning.event.genomeResourcesUnavailable":
    "This Playbook cannot be executed with the exact same input because some genome or sequence resources of the event are not available to you.",
  "playbook.execution.warning.demo.fileResourcesUnavailable":
    "This Playbook cannot be executed with the exact demo input because some file resources of the demo data are not available to you.",
  "playbook.execution.warning.demo.genomeResourcesUnavailable":
    "This Playbook cannot be executed with the exact demo input because some genome or sequence resources of the demo data are not available to you.",
  "playbook.execution.warning.default.fileResourcesUnavailable":
    "This Playbook cannot be populated with the exact configured default values because some file resources are not available to you.",
  "playbook.execution.warning.default.genomeResourcesUnavailable":
    "This Playbook cannot be populated with the exact configured default values because some genome or sequence resources are not available to you.",

  /* JOBS */
  "job.execution.statusUpdate": "{jobName} is <emph>{status}</emph>",
  "job.execution.failed": "{jobName} has <emph>failed</emph>",
  "job.submission.successTitle": "BLAST Job Submitted",
  "job.submission.successMessage":
    "The <link>BLAST job</link> was successfully submitted. You will receive a notification when the job is complete.",

  /* PROJECT */
  "activity.project.created": `Created{publicProject, select, true { shared} other {}} {projectLabel} {projectLink}
        {publicProject, select,
            true {based on user group ''<emph>{usergroup}</emph>''}
            other {with {usersCount, plural,
                =0 {no members }
                other {{membersList}}
            }
        }
    }`,
  "activity.project.addedMembers": "Added {projectLabel} member(s) {members}",
  "activity.project.addedYou": "You have been added to {projectLink}",
  "activity.project.promotedMembers": "Promoted {members} to {projectLabel} admin",
  "activity.project.promotedYou": "You have been promoted to {projectLabel} admin for {projectLink}",
  "activity.project.demotedMembers": "Revoked admin rights for {members}",
  "activity.project.demotedYou": "Your admin rights have been revoked for {projectLink}",
  "activity.project.removedMembers": "Removed member(s) {members}",
  "activity.project.removedYou": "You have been removed from {projectLink}",
  "activity.project.savedItem":
    "Added {count, plural, one {a} other {#}} new{shared, select, true { shared} false { private} other {}} {itemLabel}",
  "activity.project.deletedItem": "Deleted {count, plural, one {a} other {#}} {itemLabel}",
  "activity.project.modifiedItem": "Modified {count, plural, one {a} other {#}} {itemLabel}",
  "activity.project.publishedItem": "Published {count, plural, one {a} other {#}} {itemLabel}",
  "activity.project.sampleParent": `{action, select, ${MESSAGE_ACTION.ADD} {Added {count, plural, one {a} other {#}} new} other {Modified {count, plural, one {a} other {#}}}} <emph>{count, plural, one {sample parent} other {sample parents}}</emph>`,
  "activity.project.modifiedProject": "Published {projectLabel} {projectLink}",
  "activity.project.editedProject": "Modified {projectLabel} properties",
  "activity.project.editedProject.notification": "Modified properties of {projectLabel} {projectLink}",
  "activity.project.executedPlaybook": "Executed Playbook <link>{playbookName}</link>",
  "activity.project.executedBlastJob": "Executed Blast Job <link>{jobName}</link>",
  "activity.project.addedFlag":
    "Added {count, plural, one {a} other {#}} genome {itemLabel} {count, plural, one {to {flagLocation}} other {}} ",
  "activity.project.deletedFlag":
    "Deleted {count, plural, one {a} other {#}} genome {itemLabel} {count, plural, one {of {flagLocation}} other {}} ",
  "activity.project.modifiedFlag":
    "Modified {count, plural, one {a} other {#}} genome {itemLabel} {count, plural, one {of {flagLocation}} other {}} ",
  "activity.project.addedCoordinateList":
    "Added {count, plural, one {a} other {#}} {itemLabel} {genome, select, undefined {} other {to {genome}}}",
  "activity.project.modifiedCoordinateList":
    "Modified {count, plural, one {a} other {#}} {itemLabel} {genome, select, undefined {} other {in {genome}}}",
  "activity.project.deletedCoordinateList":
    "Removed {count, plural, one {a} other {#}} {itemLabel} {genome, select, undefined {} other {from {genome}}}",

  /* ACTIVITIES */
  "activities.delete.successTitle": "{activityType} Deleted",
  "activities.delete.successMessage":
    "<emph>{activitiesCount} {activityType}</emph> {activitiesCount, plural, one {was} other {were}} successfully deleted",
  "activities.delete.errorTitle": "{activityType} Deletion Failed",
  "activities.delete.errorMessage": "Deletion of <emph>{activitiesCount} {activityType}</emph> failed",

  /* RESTRICTED ACCESS */
  "restriction.unauthorized.title": "Unauthorized",
  "restriction.genome.unauthorized.message": "You have no access to the Reference Genome of this group of variants",

  /* SAMPLES */
  // FIXME Pluralization of sample parent name in parent.ts. How do we handle this case?
  "samples.editing.save.successTitle": "Saved {sampleCount, plural, one {Sample} other {Samples}}",
  "samples.editing.save.successMessage": "Updated {sampleCount, plural, one {# sample} other {# samples}}",

  /* DATA EDITING */
  "saveDataEditing.successTitle": "Data Updated",
  "saveDataEditing.successMessage": "Updated {rowDataLength, plural, one {# row} other {# rows}}",

  /* DATA EXPORT */
  "export.sequences.warningTitle": "Partial Sequence Copied",
  "export.sequences.warningMessage":
    "The first {maxExportLength} characters of the sequence were copied to the clipboard",
  "export.sequences.successTitle": "Sequence Copied",
  "export.sequences.successMessage": "The sequence was successfully copied to the clipboard",

  /* ADMINISTRATION */
  // USER ADMINISTRATION
  "admin.addUser.successTitle": "User Added",
  "admin.addUser.successMessage": "{firstName} {lastName} was successfully added",
  "admin.addUsers.successTitle": "{userCount, plural, one {User} other {Users}} Added",
  "admin.addUsers.successMessage": "{userCount, plural, one {# user was} other {# users were}} successfully added",
  "admin.userGroup.created.successTitle": "User Group Created",
  "admin.userGroup.created.successMessage":
    "Created user group <emph>{usergroupName}</emph> with {userCount, plural, one {# user} other {# users}}" +
    "{trackerLicensed, select, true {, {sampleTypesCount, plural, one {# sample type} other {# sample types}}} other {}}" +
    " and {genomesCount, plural, one {# genome} other {# genomes}}",
  "admin.userGroup.deleted.successTitle": "User Group Deleted",
  "admin.userGroup.deleted.successMessage": "The user group ''{userGroupName}'' was successfully deleted",

  // ROLES ADMINISTRATION
  "admin.role.created.successTitle": "Role Created",
  "admin.role.created.successMessage": "<emph>{roleName}</emph> was successfully created",
  "admin.role.modified.successTitle": "Role Modified",
  "admin.role.modified.successMessage": "<emph>{roleName}</emph> was successfully modified",

  // LOST INTERNET CONNECTION
  "admin.noInternet.infoTitle": "Internet Connection",
  "admin.noInternet.infoMessage": "The server does not have access to the Internet",

  // NCBI ADMIN
  "admin.ncbi.download.started.successTitle": "NCBI Agent",
  "admin.ncbi.download.started.successMessage": "The download from NCBI has started",

  "admin.ncbi.update.genomeListing.infoTitle": "NCBI Genome Listing",
  "admin.ncbi.update.genomeListing.started.infoMessage": "The download of the NCBI genome list has started",
  "admin.ncbi.update.genomeListing.finished.infoMessage": "The download of the NCBI genome list has completed",

  // PLAYBOOK ADMIN
  "admin.playbook.category.updated.successTitle": "Playbook Category Updated",
  "admin.playbook.category.updated.successMessage": "The Playbook category {categoryName} was successfully updated",
  "admin.playbook.category.deleted.successTitle":
    "Playbook {categoryCount, plural, one {Category} other {Categories}} Deleted",
  "admin.playbook.category.deleted.successMessage":
    "The Playbook {categoryCount, plural, one {category was} other {categories were}} successfully deleted",
  "admin.playbook.saved.successTitle": "Playbook Saved",
  "admin.playbook.saved.successMessage":
    "The Playbook ''<emph>{playbookName}</emph>'' was successfully saved. {editMode, select, true {To grant access to this Playbook you must associate it with a <link>user role</link>.} other {}}",

  // SAMPLE ADMINISTRATION
  "admin.sample.attribute.created.successTitle": "Attribute Created",
  "admin.sample.attribute.created.successMessage": "<emph>{attributeName}</emph> was successfully created",
  "admin.sample.attribute.modified.successTitle": "Attribute Modified",
  "admin.sample.attribute.modified.successMessage": "<emph>{attributeName}</emph> was successfully modified",
  "admin.sample.attribute.deleted.successTitle": "Attribute Deleted",
  "admin.sample.attribute.deleted.successMessage":
    "{attributeCount, plural, one {# attribute was} other {# attributes were}} successfully deleted",
  "admin.sample.type.created.successTitle": "Sample Type Created",
  "admin.sample.type.created.successMessage": "{sampleType} was successfully created",
  "admin.sample.type.updated.successTitle": "Sample Type Updated",
  "admin.sample.type.updated.successMessage": "{sampleType} was successfully updated",
  "admin.sample.type.deleted.successTitle": "Sample Type Deleted",
  "admin.sample.type.deleted.successMessage":
    "{sampleTypesCount, plural, one {# sample type was} other {# sample types were}} successfully deleted",
  "admin.sample.parent.created.successTitle": "Parent Type Created",
  "admin.sample.parent.created.successMessage": "{parentType} was successfully created",
  "admin.sample.parent.updated.successTitle": "Parent Type Updated",
  "admin.sample.parent.updated.successMessage": "{parentType} was successfully updated",
  "admin.sample.parent.deleted.successTitle": "Parent Type Deleted",
  "admin.sample.parent.deleted.successMessage":
    "{parentTypesCount, plural, one {# parent type was} other {# parent types were}} successfully deleted",
  "admin.sample.parent.template.warnMessage":
    "This parent type is used in multiple sample types. Changing defaults or templates will apply to all usages.",

  /*  FORMS */
  "form.input.tooltip.mandatory": "You must provide a value for this field",
  "form.input.placeholder.optional": "Auto-generated if empty",
  "form.input.placeholder.mandatory": "Enter a value",
  "form.input.required": "Input is required", // for text, number, date, ...
  "form.selection.required": "A selection is required", // for select drop down
  "form.input.noSpecialChars": 'Input must not contain the following characters: :*?"<>|/\\',
  "form.input.noBlanks": "Input must not contain any blanks",
  "form.input.onlySimpleChars": "Only alphanumeric characters, blanks, underscore and dash are allowed",
  "form.input.unparseableDate": "The date could not be parsed, please use a standard format",
  "form.input.placeholder": "Placeholder syntax ''{forbiddenText}'' is not allowed in ''{name}''", // when %{ is in a value feed to the perl agents
  // generify the following message if required
  "form.input.invalidNumOfDays": "Expiration date is limited to one year.", // identical string used in UserValidator.java
  "form.input.selectedBeforeToday": "The expiration date must not be in the past.", // identical string used in UserValidator.java
  "form.input.invalidEmail": "Not a valid email address",
  "form.input.invalidUrl": "The URL must start with a protocol e.g. https://",
  "form.restToken.explanationMessage":
    "The REST API requires a security token for authentication and to associate API requests to your user account.",

  // Sequence Upload
  "upload.database.created.successTitle": "Database Created",
  "upload.database.created.successMessage": "Upload Database <emph>{mnemonic} ({name})</emph> was successfully created",
  "upload.database.sequence.deletion.successTitle": "{sequenceCount, plural, one {Sequence} other {Sequences}} Deleted",
  "upload.database.sequence.deletion.successMessage":
    "The selected {sequenceCount, plural, one {sequence was} other {sequences were}} successfully deleted",
  "upload.database.sequence.successTitle": "Sequences Uploaded",
  "upload.database.sequence.successMessage": "The sequences have been successfully uploaded",

  // Coordinate Lists
  "coordList.upload.successTitle": "Coordinate List Uploaded",
  "coordList.upload.successMessage": "The coordinate list has been successfully uploaded",

  /* GENERIC */
  "noContent.datasets": "There are no datasets available",
  "noContent.samples": "There is no sample available",
  "noContent.parentGenes": "There are no parent genes available",
  "noContent.relatedGenes": "There are no related genes available",
  "noContent.genomicAnnotations": "There are no genomic annotations available",
  "noContent.nucleotideContent": "There is no nucleotide feature content available",
  "noContent.proteinContent": "There is no protein content available",
  "noContent.pathways": "There are no related pathways available",
  "noContent.phenotypes": "There are no phenotypes available",
  "noContent.relatedProteins": "There are no related proteins available",
  "noContent.proteinFeaturesContent": "There is no protein feature content available",
  "noContent.relatedProjects": "There are no related projects available",
  "noContent.relatedTranscripts": "There are no related transcripts available",

  /*  OTHER */
  "more.error.details": "See more details about the error",
  "ncbiagent.execution.success": "See completed {genomesCount, plural, =0 {genome} one {genome} other {genomes}}",
  "notifications.watchAlerts.errorTitle": "An Error Occurred",
  "notifications.subscription.errorTitle": "Live notifications are currently not available",
  "error.single.genome": "Only one genome can be selected",

  // WEBSOCKET CONNECTION
  "websocket.reconnect.successTitle": "Notifications Enabled",
  "websocket.reconnect.successMessage": "Live notifications have been restored",
  "websocket.delayed.errorTitle": "Notifications Disabled",
  "websocket.delayed.errorMessage":
    "The connection to the server was lost. This may be due to a server restart. You will be informed when live notifications are enabled again.",
  "websocket.file.delayedExport": "The export file is getting prepared for you. You will be notified when it is ready.",
  "websocket.file.exportReady": "The export file <emph>{filename}</emph> is ready for download.",

  // BROWSER COMPATIBILITY
  "browserCompatibility.limitedSupport.warningTitle": "Limited Support",
  "browserCompatibility.limitedSupport.warningMessage":
    "You are using a web browser that is not fully supported by this application. If you experience problems or slow performance, please switch to a <link>recommended web browser</link>.",

  /* DEMO */
  "some.more.complex.message":
    "Hello, {name}, you have {itemCount, plural, =0 {no items} one {# item} other {# items}}",
};
